import './participantAgeModal.scss';

import { FormEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  ButtonVariant,
  Input,
  InputType,
  Modal,
  Size,
} from '@eliberty/design-system-react';

import { ResponsiveSize } from '~constants';
import { findModalPortal } from '~helpers';
import { useMediaQuery } from '~hooks';

interface Props {
  open: boolean;
  minMaxAge?: { min: number; max: number };
  onClose: () => void;
  onSubmit: (age: string) => void;
}

const ParticipantAgeModal = ({ open, minMaxAge, onClose, onSubmit }: Props) => {
  const [age, setAge] = useState('');
  const [isAgeOutOfRange, setIsAgeOutOfRange] = useState(false);
  const intl = useIntl();
  const isMobile = useMediaQuery(ResponsiveSize.SCREEN_S_MOBILE);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const ageInt = Number.parseInt(age);
    if (minMaxAge && (ageInt < minMaxAge.min || ageInt > minMaxAge.max)) {
      setIsAgeOutOfRange(true);
      return;
    }
    onSubmit(age);
    onClose();
  };

  const helperText =
    minMaxAge &&
    intl.formatMessage(
      { defaultMessage: 'Age must be between {min} and {max} years old.', id: 'ageBetween' },
      minMaxAge,
    );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={intl.formatMessage({ defaultMessage: 'Add participant', id: 'addParticipant' })}
      portalProps={{ target: findModalPortal() }}
      centered
    >
      <Modal.Body>
        <form onSubmit={handleSubmit} className='ParticipantAgeModal'>
          <div className='ParticipantAgeModal_content'>
            <div className='ParticipantAgeModal_label'>
              <FormattedMessage
                id='howOldIsTheParticipant'
                defaultMessage='How old is this participant ?'
              />
            </div>
            <Input
              label={intl.formatMessage({ defaultMessage: 'Age', id: 'age' })}
              name='age'
              value={age}
              onChange={setAge}
              type={InputType.Text}
              pattern='^[0-9]*$'
              helperText={helperText}
              helperIconName={isAgeOutOfRange ? 'status-warningCircle' : 'status-infoCircle'}
              errorText={isAgeOutOfRange ? helperText : undefined}
              data-autofocus
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type='button'
          onClick={onClose}
          size={Size.M}
          fullWidth={isMobile}
          variant={ButtonVariant.Outlined}
          data-testid='ParticipantAgeModal_cancelButton'
        >
          <FormattedMessage id='cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          type='submit'
          leadingIcon='action-plus'
          size={Size.M}
          fullWidth={isMobile}
          disabled={!age}
          onClick={handleSubmit}
          data-testid='ParticipantAgeModal_submitButton'
        >
          <FormattedMessage id='addParticipant' defaultMessage='Add participant' />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ParticipantAgeModal;
